import * as React from 'react';
import { AppBar, Menu, MenuItem, Typography, IconButton, Toolbar } from '@mui/material';
import { Icon } from '../../lib/icons';
import * as PXBColors from '@brightlayer-ui/colors';
import { assertUnreachable } from '../../lib';
import { UserMenuToolbar } from '../UserMenuToolbar/UserMenuToolbar';

export enum ToolbarColor {
    Error = 'Error',
    Primary = 'Primary',
    Default = 'Default',
}

type materialColor = 'inherit' | 'primary' | 'secondary' | 'default';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getStyleColors = (
    color: ToolbarColor
): { themeColor: materialColor; foregroundColor: string; backgroundColor: string } => {
    switch (color) {
        case ToolbarColor.Error:
            return {
                themeColor: 'primary',
                foregroundColor: PXBColors.white[50],
                backgroundColor: PXBColors.red[500],
            };
        case ToolbarColor.Primary:
            return {
                themeColor: 'primary',
                foregroundColor: PXBColors.white[50],
                backgroundColor: PXBColors.blue[500],
            };
        case ToolbarColor.Default:
            return {
                themeColor: 'default',
                foregroundColor: PXBColors.black[500],
                backgroundColor: PXBColors.white[50],
            };
        default:
            return assertUnreachable(color);
    }
};

export const GenericToolbar: React.FC<{
    page?: string;
    title?: string;
    showBackButton?: boolean;
    backButtonOnClick?: ((event: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
    color?: ToolbarColor;
    subtitle?: string;
    menuItems?: Array<{ label: string; onClick: () => void }>;
    customIcon?: JSX.Element;
    tabs?: string[];
    handleChange?: any;
}> = (props) => {
    const [menuAnchor, setMenuAnchor]: [Element | null, Function] = React.useState(null);

    const colors = getStyleColors(props.color || ToolbarColor.Default);

    return (
        <>
            <AppBar
                position="sticky"
                color={colors.themeColor}
                style={{ background: colors.backgroundColor, zIndex: 500 }}
            >
                <Toolbar style={{ paddingRight: 16 }}>
                    {props.showBackButton && (
                        <IconButton onClick={props.backButtonOnClick} data-testid="app-bar-back-arrow" size="large">
                            <Icon.ArrowBack style={{ color: colors.foregroundColor }} />
                        </IconButton>
                    )}
                    <div style={{ flex: '1' }} data-testid="app-bar">
                        <Typography variant="h6" style={{ fontWeight: 600, lineHeight: 1 }}>
                            {props.title}
                        </Typography>

                        {props.subtitle}
                    </div>
                    <div>
                        {props.menuItems && props.menuItems.length > 0 && (
                            <IconButton onClick={(evt): void => setMenuAnchor(evt.currentTarget)} size="large">
                                <Icon.MoreVert style={{ color: colors.foregroundColor }} />
                            </IconButton>
                        )}
                    </div>
                    {props.customIcon}
                    <UserMenuToolbar />
                </Toolbar>
            </AppBar>
            {props.menuItems && props.menuItems.length > 0 && Boolean(menuAnchor) && (
                <Menu open={Boolean(menuAnchor)} anchorEl={menuAnchor} onClose={(): void => setMenuAnchor(null)}>
                    {props.menuItems.map((menuItem, index) => (
                        <MenuItem
                            key={index}
                            onClick={(): void => {
                                setMenuAnchor(null);
                                menuItem.onClick();
                            }}
                        >
                            {menuItem.label}
                        </MenuItem>
                    ))}
                </Menu>
            )}
        </>
    );
};
